import { NAMEPLATE, type NameplateBarStyle } from '../../../shared/config/nameplate.config';

/**
 * Overhead plate bars (HP, MP, cast) built from sprites — plan 13 §12, lane W.
 *
 * Pixi 8 keeps one instruction set for the whole stage and rebuilds it on any
 * frame where a Graphics is redrawn or a `visible` flag flips. Plates used to
 * redraw a rounded Graphics whenever their value moved (mana regen, a cast
 * filling, a mob losing HP), so a fight rebuilt the stage on most frames. A
 * plate bar is three sprites on one shared white texture — outline, track,
 * fill — and every change is a transform, tint or alpha: the fill's width is a
 * scale, its colour a tint, hiding is alpha 0. None of those rebuild the stage.
 *
 * Square corners and a 1 px outline by design (DJ, 2026-10-07: "our plates
 * should probably be simple"); the stylised look is for the HUD.
 */
export interface PlateBarStyle {
  width: number;
  height: number;
  bgColor: number;
  bgAlpha: number;
  borderColor: number;
  borderWidth: number;
  borderAlpha: number;
  depth: number;
}

export interface IPlateBar {
  /** Top-left of the track in world px. A move, never a redraw. */
  setPosition(x: number, y: number): void;
  /** Fill fraction (clamped to 0..1) and colour: a scale and a tint. */
  setFill(fraction: number, color: number): void;
  /** Track width in world px (mob bars follow their sprite): a scale. */
  setWidth(width: number): void;
  /** Shown or hidden by alpha; a `visible` flip would rebuild the stage. Starts hidden. */
  setShown(shown: boolean): void;
  /** Strength while shown, 0..1 (the overhead layout dims crowded bars).
   *  An alpha, like hide/show. Default 1. */
  setAlpha(alpha: number): void;
  destroy(): void;
}

type PixiFactory = (style: PlateBarStyle) => IPlateBar;

let pixiFactory: PixiFactory | null = null;

export function registerPixiPlateBarFactory(fn: PixiFactory): void {
  pixiFactory = fn;
}

export function createPlateBar(style: PlateBarStyle): IPlateBar {
  if (!pixiFactory) {
    throw new Error('[createPlateBar] Pixi factory not registered. Call registerAllPixiFactories() at boot.');
  }
  return pixiFactory(style);
}

export function clampFraction(fraction: number): number {
  return Number.isFinite(fraction) ? Math.max(0, Math.min(1, fraction)) : 0;
}

/** HP fill colour by threshold — the one palette mobs and players share. */
export function hpFillColor(fraction: number): number {
  const colors = NAMEPLATE.barColors;
  if (fraction < NAMEPLATE.barThresholds.critical) return colors.hpCritical;
  if (fraction < NAMEPLATE.barThresholds.warn) return colors.hpWarn;
  return colors.hpHealthy;
}

type BarLook = Pick<NameplateBarStyle, 'width' | 'height' | 'bgColor' | 'bgAlpha' | 'borderColor' | 'borderWidth' | 'borderAlpha'>;

/** A NAMEPLATE bar style as a plate-bar style at `depth`. */
export function plateStyle(bar: BarLook, depth: number): PlateBarStyle {
  return {
    width: bar.width, height: bar.height,
    bgColor: bar.bgColor, bgAlpha: bar.bgAlpha,
    borderColor: bar.borderColor, borderWidth: bar.borderWidth, borderAlpha: bar.borderAlpha,
    depth,
  };
}
